import { useEffect, useMemo, useRef, useState } from 'react'
import {
  IconChevronDown, IconCheck, IconDotsVertical, IconFolder,
  IconFolderOpen, IconFolderPlus, IconInbox, IconPencil, IconPlus, IconTrash,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { folderApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import type { Folder } from '@/api/types'
import { tr } from '@/i18n/tr'
import {
  Button, IconButton, Menu, MenuDivider, MenuItem, Modal, TextField, cx, modals, notifications,
} from '@/ui'
import './folders.css'

/** Ağaca çevrilmiş klasör. */
interface Dugum extends Folder {
  cocuklar: Dugum[]
}

/** Düz listeyi ağaca çevirir; `path` alanına göre sıralar. */
function agacKur(duz: Folder[]): Dugum[] {
  const harita = new Map<string, Dugum>()
  duz.forEach((f) => harita.set(f.id, { ...f, cocuklar: [] }))

  const kokler: Dugum[] = []
  harita.forEach((d) => {
    const ebeveyn = d.parentId ? harita.get(d.parentId) : null
    if (ebeveyn) ebeveyn.cocuklar.push(d)
    else kokler.push(d)
  })

  const sirala = (l: Dugum[]) => {
    l.sort((a, b) => a.name.localeCompare(b.name, 'tr'))
    l.forEach((d) => sirala(d.cocuklar))
  }
  sirala(kokler)
  return kokler
}

/**
 * Klasörün kullanıcıya gösterilecek yolu: "Sözleşmeler / 2026".
 *
 * <p>DİKKAT: {@code Folder.path} bunun için KULLANILAMAZ. O alan sunucunun
 * materialized path'idir ve ata kimliklerinden oluşur
 * ({@code /6eaa5cc3.../}) — alt ağaç sorgularını hızlandırmak içindir,
 * gösterim için değil. Ham haliyle gösterilirse kullanıcı ekranda UUID görür.
 */
export function gorunenYol(hepsi: Folder[], id: string): string {
  const harita = new Map(hepsi.map((f) => [f.id, f]))
  const parcalar: string[] = []
  let gecerli = harita.get(id)
  // Bozuk veriye karşı tavan: döngü olursa sonsuza kadar dönmesin.
  let tavan = 32
  while (gecerli && tavan-- > 0) {
    parcalar.unshift(gecerli.name)
    gecerli = gecerli.parentId ? harita.get(gecerli.parentId) : undefined
  }
  return parcalar.join(' / ')
}

/**
 * Proje içi klasör ağacı.
 *
 * <p>Notlar ve dosyalar aynı klasör ağacını paylaşır — bir klasörün hem notu
 * hem dosyası olabilir. Bu yüzden bileşen `sayac` ile hangi sayının
 * gösterileceğini alır: not listesinde not sayısı, dosya listesinde dosya
 * sayısı anlamlıdır; ikisini birden göstermek gürültü olurdu.
 */
export function FolderTree({
  projectId, secili, onSec, canEdit, sayac, toplam, footer,
}: {
  projectId: string
  secili: string | null
  onSec: (folderId: string | null) => void
  canEdit: boolean
  sayac: 'not' | 'dosya'
  /** "Tümü" satırındaki sayı (projedeki toplam). */
  toplam?: number
  /** Panelin altında duran ek içerik (Dosyalar: depolama özeti). */
  footer?: React.ReactNode
}) {
  const qc = useQueryClient()
  const [acik, setAcik] = useState<Set<string>>(new Set())
  // Ad penceresi: yeni klasör (parentId) ya da yeniden adlandırma (folder).
  const [adPenceresi, setAdPenceresi] = useState<
    { tur: 'yeni'; parentId: string | null } | { tur: 'ad'; folder: Folder } | null
  >(null)
  const [ad, setAd] = useState('')

  const query = useQuery({
    queryKey: ['folders', projectId],
    queryFn: () => folderApi.list(projectId),
  })

  const agac = useMemo(() => agacKur(query.data ?? []), [query.data])

  /**
   * Ağaç ilk yüklendiğinde TAMAMEN AÇIK gelir.
   *
   * <p>Açılma durumu bileşen yereldir ve sayfalar arası gezinince sıfırlanır.
   * Kapalı başlasaydı, iç içe bir klasördeki not "kaybolmuş" görünürdü:
   * kullanıcı onu görmek için her seferinde ataları tek tek açmak zorunda
   * kalırdı. Proje ağaçları küçük olduğu için açık başlamak daha doğru bir
   * varsayılan; kullanıcının elle kapattıkları korunur.
   */
  const ilkAcilisYapildi = useRef<string | null>(null)
  useEffect(() => {
    if (!query.data || ilkAcilisYapildi.current === projectId) return
    ilkAcilisYapildi.current = projectId
    setAcik(new Set(query.data.map((f) => f.id)))
  }, [query.data, projectId])

  /**
   * Klasör seçmek aynı zamanda AÇAR.
   *
   * <p>Alt klasörü olan bir klasöre tıklayıp hiçbir şeyin açılmaması,
   * kullanıcıya "burada alt klasör yok" izlenimi verir; ok işaretini ayrıca
   * bulup tıklaması gerektiğini kimse tahmin etmiyor.
   */
  const sec = (id: string | null) => {
    onSec(id)
    if (id) setAcik((s) => new Set(s).add(id))
  }

  const tazele = () => {
    qc.invalidateQueries({ queryKey: ['folders', projectId] })
    qc.invalidateQueries({ queryKey: ['notes', projectId] })
    qc.invalidateQueries({ queryKey: ['files', projectId] })
  }

  const olustur = useMutation({
    mutationFn: ({ ad, parentId }: { ad: string; parentId: string | null }) =>
      folderApi.create(projectId, ad, parentId),
    onSuccess: (f) => {
      tazele()
      if (f.parentId) setAcik((s) => new Set(s).add(f.parentId!))
      setAdPenceresi(null)
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const adlandir = useMutation({
    mutationFn: ({ id, ad }: { id: string; ad: string }) =>
      folderApi.update(projectId, id, { name: ad }),
    onSuccess: () => { tazele(); setAdPenceresi(null) },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const sil = useMutation({
    mutationFn: (id: string) => folderApi.remove(projectId, id),
    onSuccess: (_d, id) => {
      if (secili === id) onSec(null)
      tazele()
      notifications.show({ color: 'green', message: 'Klasör silindi.' })
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const kaydet = () => {
    const v = ad.trim()
    if (!v || !adPenceresi) return
    if (adPenceresi.tur === 'yeni') olustur.mutate({ ad: v, parentId: adPenceresi.parentId })
    else if (v !== adPenceresi.folder.name) adlandir.mutate({ id: adPenceresi.folder.id, ad: v })
    else setAdPenceresi(null)
  }

  const satir = (d: Dugum, derinlik: number) => {
    const acikMi = acik.has(d.id)
    const cocukVar = d.cocuklar.length > 0
    const adet = sayac === 'not' ? d.noteCount : d.fileCount
    const aktif = secili === d.id

    return (
      <div key={d.id} role="none">
        <div className={cx('nx-folder-row', aktif && 'nx-folder-row--active')} style={{ paddingLeft: 2 + derinlik * 16 }}>
          <button type="button" className="nx-folder-chev" aria-label={acikMi ? 'Kapat' : 'Aç'} aria-expanded={acikMi}
            style={{ visibility: cocukVar ? 'visible' : 'hidden' }} tabIndex={cocukVar ? 0 : -1}
            onClick={() => setAcik((s) => {
              const y = new Set(s)
              if (acikMi) y.delete(d.id)
              else y.add(d.id)
              return y
            })}>
            <IconChevronDown size={14} stroke={2} style={{ transform: acikMi ? undefined : 'rotate(-90deg)' }} />
          </button>

          <button type="button" className="nx-folder-btn" aria-current={aktif ? 'true' : undefined} onClick={() => sec(d.id)}>
            {aktif ? <IconFolderOpen size={17} stroke={1.8} aria-hidden="true" /> : <IconFolder size={17} stroke={1.8} aria-hidden="true" />}
            <span className="nx-folder-btn__name">{d.name}</span>
            {adet > 0 && <span className="nx-folder-btn__count nx-nums">{adet}</span>}
          </button>

          {canEdit && (
            <Menu align="end" width={210} trigger={
              <IconButton label="Klasör işlemleri" size="sm" className="nx-folder-menu"><IconDotsVertical size={15} /></IconButton>
            }>
              <MenuItem icon={<IconFolderPlus size={16} />} onSelect={() => { setAd(''); setAdPenceresi({ tur: 'yeni', parentId: d.id }) }}>
                Alt klasör ekle
              </MenuItem>
              <MenuItem icon={<IconPencil size={16} />} onSelect={() => { setAd(d.name); setAdPenceresi({ tur: 'ad', folder: d }) }}>
                {tr.common.rename}
              </MenuItem>
              <MenuDivider />
              <MenuItem danger icon={<IconTrash size={16} />}
                onSelect={() => modals.openConfirmModal({
                  title: 'Klasör silinsin mi?',
                  children: <><b>{d.name}</b> silinecek. İçindeki not ve dosyalar silinmez, proje köküne taşınır.</>,
                  labels: { confirm: tr.common.delete, cancel: tr.common.cancel },
                  confirmProps: { color: 'red' },
                  onConfirm: () => sil.mutate(d.id),
                })}>
                {tr.common.delete}
              </MenuItem>
            </Menu>
          )}
        </div>

        {acikMi && d.cocuklar.map((c) => satir(c, derinlik + 1))}
      </div>
    )
  }

  const kaydediyor = olustur.isPending || adlandir.isPending
  return (
    <aside className="nx-folder-panel" aria-label="Klasörler">
      <div className="nx-folder-panel__head">
        <span>Klasörler</span>
        {canEdit && (
          <IconButton label="Yeni klasör" size="sm" onClick={() => { setAd(''); setAdPenceresi({ tur: 'yeni', parentId: null }) }}>
            <IconPlus size={16} stroke={2.2} />
          </IconButton>
        )}
      </div>

      <div className={cx('nx-folder-row', secili === null && 'nx-folder-row--active')}>
        <span className="nx-folder-chev" aria-hidden="true" />
        <button type="button" className="nx-folder-btn" aria-current={secili === null ? 'true' : undefined} onClick={() => sec(null)}>
          <IconInbox size={17} stroke={1.8} aria-hidden="true" />
          <span className="nx-folder-btn__name">{tr.common.all}</span>
          {!!toplam && <span className="nx-folder-btn__count nx-nums">{toplam}</span>}
        </button>
      </div>

      {agac.map((d) => satir(d, 0))}

      {query.data?.length === 0 && (
        <p className="nx-folder-panel__empty">
          Henüz klasör yok.{canEdit && ' Yukarıdaki + ile ekleyebilirsiniz.'}
        </p>
      )}

      {footer}

      <Modal opened={!!adPenceresi} onClose={() => setAdPenceresi(null)} size="sm"
        title={adPenceresi?.tur === 'ad' ? 'Klasörü yeniden adlandır' : adPenceresi?.parentId ? 'Alt klasör ekle' : 'Yeni klasör'}
        footer={<>
          <Button variant="secondary" onClick={() => setAdPenceresi(null)}>{tr.common.cancel}</Button>
          <Button type="submit" form="folder-name" disabled={!ad.trim()} loading={kaydediyor}>
            {adPenceresi?.tur === 'ad' ? tr.common.save : tr.common.create}
          </Button>
        </>}>
        <form id="folder-name" onSubmit={(e) => { e.preventDefault(); kaydet() }}>
          <TextField label="Klasör adı" placeholder="Örn. Sözleşmeler" value={ad} maxLength={120} autoFocus
            onChange={(e) => setAd(e.currentTarget.value)} />
        </form>
      </Modal>
    </aside>
  )
}

/**
 * "Klasöre taşı" seçicisi.
 *
 * <p>Ağaç yerine düz yol listesi gösterir: taşıma tek seferlik bir karardır,
 * kullanıcıyı ağaçta gezdirmek yerine hedefi doğrudan seçtirmek daha hızlıdır.
 */
export function FolderPicker({
  projectId, opened, onClose, mevcutId, onSec, baslik,
}: {
  projectId: string
  opened: boolean
  onClose: () => void
  mevcutId: string | null
  onSec: (folderId: string | null) => void
  baslik: string
}) {
  const query = useQuery({
    queryKey: ['folders', projectId],
    queryFn: () => folderApi.list(projectId),
    enabled: opened,
  })

  const secenekler = useMemo(() => {
    const hepsi = query.data ?? []
    return hepsi
      .map((f) => ({ ...f, gosterim: gorunenYol(hepsi, f.id) }))
      .sort((a, b) => a.gosterim.localeCompare(b.gosterim, 'tr'))
  }, [query.data])

  const sec = (id: string | null) => { onSec(id); onClose() }
  const secenek = (id: string | null, ad: string, icon: React.ReactNode) => (
    <button key={id ?? 'kok'} type="button" className={cx('nx-folder-pick', mevcutId === id && 'nx-folder-pick--active')}
      aria-current={mevcutId === id ? 'true' : undefined} onClick={() => sec(id)}>
      {icon}
      <span className="nx-folder-btn__name">{ad}</span>
      {mevcutId === id && <span className="nx-folder-pick__now"><IconCheck size={13} stroke={2.4} aria-hidden="true" />şu an</span>}
    </button>
  )

  return (
    <Modal opened={opened} onClose={onClose} title={baslik} size="sm">
      <div className="nx-folder-picks">
        {secenek(null, 'Proje kökü', <IconInbox size={17} stroke={1.8} aria-hidden="true" />)}
        {secenekler.map((f) => secenek(f.id, f.gosterim, <IconFolder size={17} stroke={1.8} aria-hidden="true" />))}
        {secenekler.length === 0 && (
          <p className="nx-folder-panel__empty">Bu projede klasör yok. Önce not ya da dosya listesinden bir klasör oluşturun.</p>
        )}
      </div>
    </Modal>
  )
}
